<template>
  <v-card>
    <v-card-title>
      <span class="headline"><v-icon>mdi-database-edit</v-icon>返品一括編集</span>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-form ref="purchaseReturnBulkEditForm" lazy-validation>
        <v-container>
          <section-block>
            <v-row v-for="(source, index) in settingSources" :key="index" dense>
              <v-col cols="3">
                <v-select
                  :items="constant.purchaseReturnBulkEditColumnsValues"
                  label="設定項目"
                  v-model="source.column"
                  :rules="[rules.required, rules.validSelectColumn]"
                  @change="onSettingColSelected(index)"
                  dense
                  filled
                  attach
                ></v-select>
              </v-col>
              <v-col
                :cols="
                  source.column === constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_QUANTITY
                    ? 2
                    : 4
                "
              >
                <v-select
                  v-if="source.column === constant.purchaseReturnBulkEditColumns.PRODUCT_CONDITION_TYPES"
                  :items="constant.productConditionTypeList"
                  label="商品状態区分"
                  v-model="source.value"
                  filled
                  dense
                  attach
                >
                </v-select>
                <v-text-field
                  v-if="source.column === constant.purchaseReturnBulkEditColumns.RETURN_QUANTITY"
                  label="返品数"
                  v-model="source.value"
                  @change="source.value = stringToNumber($event)"
                  :rules="[rules.isNumber, rules.maxLength(8), rules.isMinNumber(1)]"
                  filled
                  dense
                >
                </v-text-field>
                <storage-field
                ref="storage"
                v-if="source.column === constant.purchaseReturnBulkEditColumns.SHIPPING_WAREHOUSE"
                v-model="source.value"
                :rules="[rules.required]"
                >
                </storage-field>
                <v-switch
                  v-if="source.column === constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_RESULT"
                  inset
                  label="メーカー検品結果"
                  v-model="source.value"
                  filled
                  dense
                >
                </v-switch>
                <v-text-field
                  v-if="source.column === constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_QUANTITY"
                  label="メーカー検品数"
                  v-model="source.value"
                  filled
                  dense
                  @change="source.value = stringToNumber($event)"
                  :rules="[rules.isNumber, rules.maxLength(8)]"
                  :disabled="quantityCopyReturn"
                >
                </v-text-field>
              </v-col>
              <v-col cols="2" v-if="source.column === constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_QUANTITY">
                <v-switch
                  inset
                  label="返品数を入力"
                  v-model="quantityCopyReturn"
                  filled
                  dense
                >
                </v-switch>
              </v-col>
              <v-col cols="1"
                ><v-btn v-if="index != 0" x-small fab dense @click="onDelSettingSource(index)">
                  <v-icon dark> mdi-minus </v-icon></v-btn
                ></v-col
              >
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-btn x-small fab dense @click="onAddSettingSource">
                  <v-icon dark> mdi-plus </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </section-block>
          <v-divider class="mt-3 pt-3"></v-divider>
          <v-row dense>
            <v-col cols="12" sm="12">
              <v-spacer></v-spacer>
              <tooltip-icon-button
                :disabled="selectedRows.length == 0"
                icon="mdi-arrow-down-bold-outline"
                @click="onBtnSetUpdate"
                >一括反映</tooltip-icon-button
              >
            </v-col>
            <v-col cols="12" sm="12" style="height: 450px">
              <ag-grid-vue
                :domLayout="domLayout"
                class="ag-theme-alpine"
                :defaultColDef="defaultColDef"
                :columnDefs="columnDefs"
                :alwaysShowHorizontalScroll="true"
                :suppressColumnVirtualisation="true"
                :pagination="true"
                paginationPageSize="10"
                :localeText="localeText"
                rowSelection="multiple"
                @grid-ready="onGridReady"
                @column-everything-changed="onColumnEverythingChanged"
                @selection-changed="onSelectionChanged"
                :columnTypes="columnTypes"
                :getRowNodeId="(data) => data.returnCommitNumber"
                :frameworkComponents="frameworkComponents"
                @cell-value-changed="onCellValueChanged"
                style="height: 100%"
                @cell-editing-started="onCellEditingStarted"
              >
              </ag-grid-vue>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn color="secondary" @click="onCancelClick">キャンセル</v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="onUpdateClick">更新</v-btn>
    </v-card-actions>
    <v-overlay :value="isLoading">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-overlay>
    <error-grid-dialog
      ref="updateErrorGrid"
      width="80%"
      height="80%"
      icon=""
      title="メーカー返品一括編集"
      btnSubmit="登録"
      :columns="errorColumns"
    ></error-grid-dialog>
  </v-card>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import { AG_GRID_LOCALE_JA } from "../../models/ag-grid/locales";
import {
  NumericColumn,
  PercentColumn,
  DateColumn,
  FullDateColumn,
  CheckmarkColumn,
  EditableColumn,
  EditableDateColumn,
  EditableCheckBoxColumn,
  EditableCustomDateColumn,
} from "../../models/ag-grid/columnTypes";
import { LockedCellRenderer, ProtectCellRenderer } from "../../models/ag-grid/cellRenderers";
import { getColumnDef } from "../../models/ag-grid/helpers";
import TooltipIconButton from "../common/TooltipIconButton.vue";
import SectionBlock from "../common/SectionBlock.vue";
import {
  DateCellEditor,
  CheckboxCellEditor,
  CustomDateCellEditor,
  NumericCellEditor,
} from "../../components/ag-grid/cellEditors";
import ErrorGridDialog from "../../components/common/ErrorGridDialog.vue";
import { statuses as ApiStatus } from "../../libs/api-client";
import Validation from "../../libs/validation";
import {} from "../../components/ag-grid/cellEditors";
import { BooleanFilter, SelectionFilter } from "../../components/ag-grid/filters";
import purchaseReturnBulkEditColumns from "../../consts/editColumns/PurchaseReturnBulkEditColumns";
import productConditionTypes from "../../consts/productConditionTypes";
import productConditionList from "../../consts/productConditionList";
import CafereoPurchaseReturnStatus from "../../consts/purchaseReturnStatus/CafereoPurchaseReturnStatuses";
import VendorpurchaseReturnStatus from "../../consts/purchaseReturnStatus/VendorPurchaseReturnStatuses";
import ReturnTypes from "../../consts/ReturnTypes";
import WarehouseCd from "../../consts/Storages";
import ConvertUtils from "../../utils/ConvertUtils";
import StorageField from "../../components/common/fields/StorageField.vue";
export default {
  name: "purchaseReturnBulkEdit",
  props: ["inputModel"],
  components: {
    AgGridVue,
    TooltipIconButton,
    ErrorGridDialog,
    SectionBlock,
    /* eslint-disable vue/no-unused-components */
    DateCellEditor,
    CheckboxCellEditor,
    CustomDateCellEditor,
    NumericCellEditor,
    StorageField,
  },
  data() {
    const checkStatuses = { true: "✔", false: "　" };
    return {
      updateModel: {},
      defaultModel: {
        productConditionType: null,
        returnQuantity: null,
        warehouseCd: null,
        makerInspectionResult: null,
        makerInspectionQuantity: null,
      },
      constant: {
        purchaseReturnBulkEditColumnsValues: purchaseReturnBulkEditColumns.all(),
        purchaseReturnBulkEditColumns: purchaseReturnBulkEditColumns,
        productConditionTypeList: productConditionTypes.all(false),
        productConditionSelectList: productConditionList.all(),
      },
      inspectedStatus: CafereoPurchaseReturnStatus.INSPECTED,
      vendorStatusList: VendorpurchaseReturnStatus.all(),

      defaultColDef: null,
      columnDefs: null,
      gridApi: null,
      columnApi: null,
      selectedRows: [],
      domLayout: null,
      localeText: AG_GRID_LOCALE_JA,
      updateList: [],
      settingSources: [],
      quantityCopyReturn: false,
      quantityCopyBad: false,
      columnTypes: {
        dpNumericColumn: NumericColumn,
        dpPercentColumn: PercentColumn,
        dpDateColumn: DateColumn,
        dpFullDateColumn: FullDateColumn,
        dpCheckmarkColumn: CheckmarkColumn,
        dpEditableColumn: EditableColumn,
        dpEditableDateColumn: EditableDateColumn,
        dpEditableCheckBoxColumn: EditableCheckBoxColumn,
        dpEditableCustomDateColumn: EditableCustomDateColumn,
      },
      frameworkComponents: {
        dpDateCellEditor: DateCellEditor,
        dpCheckboxCellEditor: CheckboxCellEditor,
        dpBooleanFilter: BooleanFilter,
        dpSelectionFilter: SelectionFilter,
        dpCellDatePicker: CustomDateCellEditor,
        dpNumericCellEditor: NumericCellEditor,
      },
      errorRows: [],
      updateSuccessRecords: [],
      rules: {
        required: Validation.required, 
        isNumber: Validation.isNumber,
        maxLength: Validation.maxLength,
        isMinNumber: Validation.isMinNumber,
        validSelectColumn: (value) => this.validSelectColumn(value),
      },
      stringToNumber: ConvertUtils.stringToNumber,
      searchFlg: false,
      propotionFlg: false,
      cafereoColumns: [
        { headerName: "登録日", field: "createDatetime", type: "dpDateColumn", hide: true },
        { headerName: "返品確認番号", field: "returnCommitNumber", pinned: "left" },
        {
          headerName: "ステータス",
          field: "returnCommitStatus",
          filter: "dpSelectionFilter",
          filterParams: { options: CafereoPurchaseReturnStatus.all() },
          valueGetter: (params) => CafereoPurchaseReturnStatus.of(params.data.returnCommitStatus),
        },
        { headerName: "仕入先略称", field: "maker" },
        {
          headerName: "商品状態区分",
          field: "productConditionType",
          filter: "dpSelectionFilter",
          filterParams: { options: productConditionList.all() },
          editable: (params) => {
            return params.data.returnCommitStatus !== 4;
          },
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: Object.keys(productConditionList.refDataArray()),
            cellEditorPopup: true,
          },
          refData: productConditionList.refDataArray(),
          cellStyle: (params) => {
            if (params.data.returnCommitStatus === 4) {
              return {};
            }
            let ret = { backgroundColor: "#FFFFCC" };
            if (params.data.updateProductConditionType) {
              ret.backgroundColor = "#CCFFCC";
            }
            return ret;
          },
        },
        { headerName: "JANCODE", field: "janCode" },
        { headerName: "タイトル", field: "title" },
        { headerName: "商品名", field: "productName" },
        { headerName: "上代", field: "retailPrice", type: "dpNumericColumn" },
        {
          headerName: "仕入掛率",
          field: "purchaseRate",
          type: "dpPercentColumn",
          valueGetter: (params) => params.data.purchaseRate.toFixed(1),
        },
        { headerName: "仕切単価", field: "unitPrice", type: "dpNumericColumn" },
        { headerName: "発注数", field: "purchaseQuantity", type: "dpNumericColumn" },
        {
          headerName: "返品数",
          field: "returnQuantity",
          type: "dpNumericColumn",
          editable: (params) => {
            return params.data.returnCommitStatus !== 4;
          },
          cellStyle: (params) => {
            if (params.data.returnCommitStatus === 4) {
              return {};
            }
            let ret = { backgroundColor: "#FFFFCC" };
            if (params.data.updateReturnQuantity) {
              ret.backgroundColor = "#CCFFCC";
            }
            return ret;
          },
        },
        {
          headerName: "倉庫",
          field: "warehouseCd",
          filter: "dpSelectionFilter",
          filterParams: { options: WarehouseCd.all() },
          editable: (params) => {
            return params.data.returnCommitStatus !== 4;
          },
          cellEditor: "agSelectCellEditor",
          cellEditorPopup: true,
          cellEditorParams: {
            values: Object.keys(WarehouseCd.refDataArray()),
            cellEditorPopup: true,
          },
          refData: WarehouseCd.refDataArray(),
          cellStyle: (params) => {
            if (params.data.returnCommitStatus === 4) {
              return {};
            }
            let ret = { backgroundColor: "#FFFFCC" };
            if (params.data.updateWarehouseCd) {
              ret.backgroundColor = "#CCFFCC";
            }
            return ret;
          },
        },
        {
          headerName: "メーカー検品結果",
          field: "makerInspectionResult",
          filter: "dpBooleanFilter",
          filterParams: { caption: checkStatuses },
          editable: true,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: Object.keys(checkStatuses),
            cellEditorPopup: true,
          },
          refData: checkStatuses,
          cellStyle: (params) => {
            let ret = { backgroundColor: "#FFFFCC" };
            if (params.data.updateMakerInspectionResult) {
              ret.backgroundColor = "#CCFFCC";
            }
            return ret;
          },
        },
        {
          headerName: "メーカー検品数",
          field: "makerInspectionQuantity",
          type: "dpNumericColumn",
          editable: true,
          cellStyle: (params) => {
            let ret = { backgroundColor: "#FFFFCC" };
            if (params.data.makerInspectionQuantity) {
              ret.backgroundColor = "#CCFFCC";
            }
            return ret;
          },
        },
        { headerName: "届先法人名", field: "delivery1", hide: true },
        { headerName: "届先名称", field: "delivery2", hide: true },
        { headerName: "登録日時", field: "createDatetime", type: "dpDateColumn", hide: true },
        { headerName: "登録者", field: "createUser", hide: true },
        { headerName: "更新日時", field: "updateDatetime", type: "dpDateColumn", hide: true },
        { headerName: "更新者", field: "updateUser", hide: true },
      ],
      errorColumns: [
        { headerName: "返品ID", field: "returnCommitNumber" },
        {
          headerName: "エラー内容",
          field: "errorMessage",
          wrapText: true,
          autoHeight: true,
          cellRenderer: function (param) {
            return param.data.errorMessage.join("<br>");
          },
        },
      ],
    };
  },
  watch: {
    inputModel(inputModel) {
      this.init(inputModel);
    },
  },
  computed: {
    isLoading() {
      return this.$store.getters["ui/isLoading"];
    },
    isQuantityDisabled() {
      if (
        this.source.returnType == ReturnTypes.CONSIGNMENT &&
        this.source.productConditionType == productConditionTypes.GOOD
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    // ****************************
    // ag-grid
    // ****************************
    setColumnDefs() {
      let columnDefs = [
        {
          headerName: "",
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          checkboxSelection: true,
          filter: false,
          resizable: false,
          sortable: false,
          pinned: "left",
          cellRenderer: (params) => {
            if (this.activeStatusTab === "unchangeApproved" && !params.data.productLocked) {
              return ProtectCellRenderer({ matcher: "purchaseLocked" })(params);
            } else {
              return LockedCellRenderer({ matcher: "purchaseLocked" })(params);
            }
          },
        },
      ];
      columnDefs = columnDefs.concat(this.cafereoColumns);
      // ag-grid内のvalidation等をセット
      getColumnDef(columnDefs, "returnQuantity").valueSetter = this.valueSetterReturnQuantity;
      getColumnDef(columnDefs, "makerInspectionQuantity").valueSetter = this.valueSetterMakerInspectionQuantity;
      return columnDefs;
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      this.init(this.inputModel);

      var allColumnIds = [];
      this.columnApi.getAllColumns().forEach(function (column) {
        allColumnIds.push(column.colId);
      });
      this.columnApi.autoSizeColumns(allColumnIds);
      this.gridApi.refreshCells();
    },
    onColumnEverythingChanged() {
      var allColumnIds = [];
      this.columnApi?.getAllColumns().forEach(function (column) {
        allColumnIds.push(column.colId);
      });
      this.columnApi?.autoSizeColumns(allColumnIds);
      this.gridApi?.refreshCells();
    },
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();
    },
    onCellEditingStarted(event) {
      const popupTop = event.api.getCellEditorInstances()[0].getGui().style.top;
      event.api.getCellEditorInstances()[0].getGui().style.top = parseInt(popupTop) - 40 + "px";
    },
    valueSetterReturnQuantity(params) {
      let isNumber = Validation.isNumber(params.newValue);
      if (isNumber !== true) {
        this.$dialog.notify.error("返品数は" + isNumber, { timeout: 2300 });
        return false;
      }
      if (Number(params.newValue) < 1) {
        this.$dialog.notify.error("1以上を入力してください", { timeout: 2300 });
        return false;
      }
      if (Number(params.data.purchaseQuantity) < Number(params.newValue)) {
        this.$dialog.notify.error("返品数は発注数を超えて入力できません", { timeout: 2300 });
        return false;
      }
      params.data.returnQuantity = Number(params.newValue);
      params.data.updateReturnQuantity = true;
      return true;
    },
    valueSetterMakerInspectionQuantity(params) {
      let isNumber = Validation.isNumber(params.newValue);
      if (isNumber !== true) {
        this.$dialog.notify.error("検品数は" + isNumber, { timeout: 2300 });
        return false;
      }
      if (String(params.newValue).length > 8) {
        this.$dialog.notify.error("8桁以下で入力してください", { timeout: 2300 });
        return false;
      }
      console.log(params.data);
      if (!params.data.makerInspectionResult || params.data.makerInspectionResult == "false") {
        this.$dialog.notify.error("検品結果にチェックしてください", { timeout: 2300 });
        return false;
      }
      params.data.makerInspectionQuantity = params.newValue ? Number(params.newValue) : null;
      params.data.updatemakerInspectionQuantity = true;
      return true;
    },
    // ****************************
    // 画面アクション定義
    // ****************************
    init(inputModel) {
      this.updateModel = this.defaultModel;
      this.gridApi.setRowData(JSON.parse(JSON.stringify(inputModel)));
      this.propotionFlg =
        this.updateModel.productConditionType == productConditionTypes.CONSIGNMENT ||
        this.updateModel.productConditionType == productConditionTypes.BUYINGSALES;
      this.updateSuccessRecords = [];
      // 初期表示時、設定項目を１つ表示する。
      this.onAddSettingSource();
      if (this.$refs.purchaseReturnBulkEditForm) {
        this.$refs.purchaseReturnBulkEditForm.resetValidation();
      }
    },
    reset() {
      this.init(this.inputModel);
      this.updateSuccessRecords = [];
    },
    onCancelClick() {
      this.$emit("onDialogClose", this.updateSuccessRecords);
    },
    async onUpdateClick() {
      var requestRecords = [];
      this.errorRows = [];
      var isValid = true;
      this.gridApi.forEachNode((record) => {
        var error = this.validateRow(record.data);
        if (error != true) {
          this.errorRows.push({
            returnCommitNumber: record.data.returnCommitNumber,
            errorMessage: error,
          });
          isValid = false;
        } else {
          requestRecords.push(this.requestFormat(record.data));
        }
      });
      if (isValid) {
        try {
          this.loadingOn();
          var param = {
            purchaseReturnList: requestRecords,
          };
          const response = await this.$store.dispatch("purchaseReturn/bulkupdate", param);
          let error = response.data?.header;
          // 一覧更新レコード
          this.searchFlg = true;
          let updateRows = [];
          this.errorRows = [];
          switch (error.resultCode) {
            case ApiStatus.consts.SUCCESS:
            case ApiStatus.consts.BUSINESS_ERROR:
            case ApiStatus.consts.ALREADY_CHANGED:
              // エラーメッセージ格納
              if (error.messages) {
                Object.keys(error.messages).forEach((key) => {
                  this.errorRows.push({
                    returnCommitNumber: key,
                    errorMessage: error.messages[key],
                  });
                });
              }
              // 一覧部の更新分のレコード設定
              if (response.data.contents) {
                this.gridApi.forEachNode((row) => {
                  response.data.contents.purchaseReturnList.forEach((purchase) => {
                    let isError = this.errorRows.some((r) => r.returnCommitNumber === row.data.returnCommitNumber);
                    if (purchase.returnCommitNumber == row.data.returnCommitNumber && !isError) {
                      this.updateSuccessRecords.push(purchase);
                      updateRows.push(purchase);
                    }
                  });
                });
              }
              // エラー表示
              if (this.errorRows.length > 0) {
                // 確認ダイアログから更新した分を削除(エラーがある場合のみ)
                this.gridApi.applyTransaction({ remove: updateRows });
                this.$refs.updateErrorGrid.open({ records: this.errorRows });
                this.$dialog.notify.error(`返品情報の更新処理に失敗したデータが存在します。ご確認ください。`, {
                  timeout: 2300,
                });
              } else {
                this.$dialog.notify.info(`返品情報を更新しました`, { timeout: 2300 });
                this.$refs.updateErrorGrid.close();
                this.$emit("onEditSubmit", this.updateSuccessRecords);
                this.reset();
              }
              break;
            default:
              this.redirectError();
              break;
          }
        } catch (error) {
          console.error("purchaseReturnBulkEdit_update::onSubmit", error);
          this.apiRequestError(error);
        } finally {
          this.loadingOff();
        }
      } else {
        this.$refs.updateErrorGrid.open({ records: this.errorRows });
        this.$dialog.notify.error(`更新データに入力エラーが存在します。ご確認ください。`, {
          timeout: 2300,
        });
      }
    },
    requestFormat(row) {
      row.productConditionType = Number(row.productConditionType);
      row.returnQuantity = Number(row.returnQuantity);
      row.warehouseCd = String(row.warehouseCd);
      console.log("row.inspectionResult:" + row.makerInspectionResult);
      row.makerInspectionResult = row.makerInspectionResult === "true" || row.makerInspectionResult === true ? true : false;
      // 値が設定されている場合数値に変換し、それ以外かつ検品結果がtrueの場合０を設定、条件に当てはまらない場合NULL
      if (row.makerInspectionQuantity) {
        row.makerInspectionQuantity = Number(row.makerInspectionQuantity);
      } else if (row.makerInspectionResult) {
        row.makerInspectionQuantity = 0;
      } else {
        row.makerInspectionQuantity = null;
      }
      return row;
    },
    onCellValueChanged(params) {
      // セルの色を変えるため項目更新をチェック
      if (params.colDef.field == "productConditionType") {
        params.data.updateProductConditionType = true;
      } else if (params.colDef.field == "returnQuantity") {
        this.onQuantityChanged(params.data);
        params.data.updateReturnQuantity = true;
      } else if (params.colDef.field == "warehouseCd") {
        params.data.updateWarehouseCd = true;;
      } else if (params.colDef.field == "makerInspectionResult") {
        params.data.updateMakerInspectionResult = true;
      } else if (params.colDef.field == "makerInspectionQuantity") {
        params.data.updateMakerInspectionQuantity = true;
      }
      let rows = [];
      rows.push(params.node);
      this.gridApi.refreshCells({ force: true, rowNodes: rows });
    },
    onSettingColSelected(index) {
      this.settingSources[index].value = "";
    },
    onAddSettingSource() {
      let settingSource = {
        column: 0,
        value: "",
      };
      this.settingSources.push(settingSource);
    },
    onDelSettingSource(index) {
      this.settingSources.splice(index, 1);
    },
    onBtnSetUpdate() {
      if (this.validate()) {
        this.selectedRows.forEach((row) => {
          this.settingSources.forEach((source) => {
            if (source.column === this.constant.purchaseReturnBulkEditColumns.PRODUCT_CONDITION_TYPES) {
              // 商品状態区分
              // ステータスが検品済みの場合、無効
              if (row.returnCommitStatus !== 4) {
                row.productConditionType = source.value;
                row.updateProductConditionType = true;
              }
            } else if (source.column === this.constant.purchaseReturnBulkEditColumns.RETURN_QUANTITY) {
              // 返品数
              // ステータスが検品済み or 返品数が発注数を超える場合、無効
              if (row.returnCommitStatus !== 4 || row.purchaseQuantity >= source.value) {
                row.returnQuantity = source.value;
                this.onQuantityChanged(row);
                row.updateReturnQuantity = true;
              }
            } else if (source.column === this.constant.purchaseReturnBulkEditColumns.SHIPPING_WAREHOUSE) {
              // 倉庫
              // ステータスが検品済みの場合、無効
              if (row.returnCommitStatus !== 4) {
                row.warehouseCd = source.value;
                row.updateWarehouseCd = true;
              }
            } else if (source.column === this.constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_RESULT) {
              // メーカー検品結果
                row.makerInspectionResult = source.value;
                row.updateMakerInspectionResult = true;
            } else if (source.column === this.constant.purchaseReturnBulkEditColumns.MAKER_INSPECTION_QUANTITY) {
              // メーカー検品数
              // 検品結果がtrueでない場合、無効
              if (row.makerInspectionResult == true) {
                if (this.quantityCopyReturn) {
                  row.makerInspectionQuantity = row.returnQuantity;
                } else {
                  row.makerInspectionQuantity = source.value;
                }
                row.updateMakerInspectionQuantity = true;
              }
            }
          });
        });
        this.gridApi.applyTransaction({ update: this.selectedRows });
        this.updateModel = this.defaultModel;
      }
    },
    // ****************************
    // 共通業務処理
    // ****************************
    onQuantityChanged(row) {
      row.unitReturnPriceTotal = row.unitPrice * row.returnQuantity;
    },
    // ****************************
    // Validation定義
    // ****************************
    validate() {
      const isValid = this.$refs.purchaseReturnBulkEditForm.validate();
      if (!isValid) {
        this.$dialog.error({ text: "入力エラーがあります", actions: ["OK"] });
      }
      return isValid;
    },
    validateRow(row) {
      var ret = true;
      var messages = [];
      // 商品状態区分
      this.setValidMessage(this.rules.required(row.productConditionType), "商品状態区分", messages);
      // 返品数
      this.setValidMessage(this.rules.required(row.returnQuantity), "返品数", messages);
      this.setValidMessage(this.rules.isNumber(row.returnQuantity), "返品数", messages);
      this.setValidMessage(this.rules.maxLength(8)(row.returnQuantity), "返品数", messages);
      this.setValidMessage(this.rules.isMinNumber(1)(row.returnQuantity), "返品数", messages);
      // メーカー検品結果
      this.setValidMessage(this.rules.required(row.makerInspectionResult), "検品結果", messages);
      // メーカー検品数
      this.setValidMessage(this.rules.isNumber(row.makerInspectionQuantity), "メーカー検品数", messages);
      this.setValidMessage(this.rules.maxLength(8)(row.makerInspectionQuantity), "メーカー検品数", messages);

      if (messages.length > 0) ret = messages;
      return ret;
    },
    validSelectColumn(value) {
      if (value == null) return true;
      if (this.settingSources.filter((settingSource) => settingSource.column === value).length > 1)
        return "同一項目が選択されています。";
      return true;
    },
    setValidMessage(check, culumnName, messages) {
      if (!(check === true)) messages.push(culumnName + "は" + check);
    },
    booleanFormat(value) {
      if (value == null || value == "") return false;
      if (value == "Y") return true;
      if (value == "N") return false;
      return Boolean(value);
    },
  },

  beforeMount() {
    this.domLayout = "autoHeight";
    this.defaultColDef = {
      filter: "agTextColumnFilter",
      resizable: true,
      sortable: true,
      suppressSizeToFit: true,
      filterParams: {
        newRowsAction: "keep",
      },
      maxWidth: 300,
    };
    this.columnDefs = this.setColumnDefs();
  },
};
</script>
